import { Alert, Box, Paper, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { validateAwsArn } from '../../pages/Accounts/CreateAccount/ManualSections/config';

interface AccountFormProps {
  onFormValidityChange: (isValid: boolean) => void;
  onAwsArnChange: (value: string) => void;
  onCompanyNameChange: (value: string) => void;
}

export const AccountForm: React.FC<AccountFormProps> = ({
  onAwsArnChange,
  onCompanyNameChange,
  onFormValidityChange,
}) => {
  const [awsArn, setAwsArn] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [arnError, setArnError] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');

  useEffect(() => {
    let arnValid = false;
    let companyNameIsValid = false;

    if (!awsArn) {
      setArnError('AWS ARN is required');
    } else if (!validateAwsArn(awsArn)) {
      setArnError('Invalid AWS ARN format');
    } else {
      setArnError('');
      arnValid = true;
    }

    if (!companyName) {
      setCompanyNameError('Company name is required');
    } else if (companyName.length < 2) {
      setCompanyNameError('Company name is too short');
    } else {
      setCompanyNameError('');
      companyNameIsValid = true;
    }

    onFormValidityChange(arnValid && companyNameIsValid);
  }, [awsArn, companyName, onFormValidityChange]);

  const handleAwsArnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAwsArn(value);
    onAwsArnChange(value);
  };

  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCompanyName(value);
    onCompanyNameChange(value);
  };

  return (
    <Box className="formContainer">
      <Paper elevation={1} className="formPaper">
        <Typography variant="h5" className="formTitle">
          Final Account Details
        </Typography>

        <Box className="inputField">
          <Typography variant="body1">AWS Role ARN</Typography>
          <TextField
            value={awsArn}
            onChange={handleAwsArnChange}
            error={!!arnError}
            helperText={
              arnError || 'Enter the ARN of the role you created earlier'
            }
            placeholder="arn:aws:iam::123456789012:role/cfx-role"
            variant="outlined"
            fullWidth
            size="small"
          />
          <Typography variant="body1" className="fieldHint">
            The ARN should be in the format:
            arn:aws:iam::123456789012:role/role-name
          </Typography>
        </Box>

        <Box className="inputField">
          <Typography variant="body1">Company Name</Typography>
          <TextField
            value={companyName}
            onChange={handleCompanyNameChange}
            error={!!companyNameError}
            helperText={
              companyNameError ||
              'The name your organization will be known as in CloudForestX'
            }
            placeholder="Acme Corporation"
            variant="outlined"
            fullWidth
            size="small"
          />
        </Box>

        <Box className="infoBox">
          <Alert severity="info" sx={{ fontSize: '11px' }}>
            Your AWS account will be associated with this company name in
            CloudForestX dashboard. This information is used for organizing and
            displaying your cost data.
          </Alert>
        </Box>
      </Paper>
    </Box>
  );
};
